import React, { useState } from 'react';
import { clientsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, Loader2, CheckCircle2, AlertTriangle, GitMerge, PlusCircle, FileWarning, Lock, RefreshCcw } from 'lucide-react';
import { useCap } from '../components/auth/RequireCap';

/**
 * WinTeam AR Customer Master import — two-phase (dry-run preview → confirm).
 *
 * Buckets:
 *   • will_create  — new single clients (no duplicates)
 *   • will_merge   — duplicate rows sharing one CustomerNumber → collapse into
 *                    one client (operator picks the primary row, reviews contacts)
 *   • needs_review — ambiguous: identical names with differing CustomerNumbers,
 *                    or near-identical names — operator decides merge vs separate
 * Nothing is written until Confirm.
 */

const ACTION_LABELS = {
  merge: 'Merge into one client',
  separate: 'Import separately',
  skip: 'Skip (do not import)',
};

function ClusterCard({ cluster, decision, onDecision, reviewMode }) {
  const action = decision?.action || cluster.suggested_action;
  const primary = decision?.primary_row_index ?? cluster.suggested_primary_index;
  const reasonLabel = {
    duplicate_rows_same_customer_number: 'Duplicate rows — same Customer #',
    differing_customer_numbers: 'Same name — different Customer #s',
    similar_names: 'Near-identical names',
  }[cluster.reason] || cluster.reason;

  return (
    <div className={`border rounded-lg p-4 ${action === 'skip' ? 'opacity-60 border-slate-200' : reviewMode ? 'border-amber-200 bg-amber-50/30' : 'border-blue-200 bg-blue-50/30'}`}
      data-testid={`import-cluster-${cluster.cluster_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <p className="font-semibold text-slate-800">{cluster.name}</p>
          <p className="text-xs text-slate-500">{cluster.rows.length} rows · <Badge variant="outline" className="text-[10px]">{reasonLabel}</Badge></p>
        </div>
        <div className="w-56">
          <Select value={action} onValueChange={(v) => onDecision({ action: v, primary_row_index: primary })}>
            <SelectTrigger className="h-9" data-testid={`cluster-action-select-${cluster.cluster_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="merge">{ACTION_LABELS.merge}</SelectItem>
              {cluster.reason !== 'duplicate_rows_same_customer_number' && (
                <SelectItem value="separate">{ACTION_LABELS.separate}</SelectItem>
              )}
              <SelectItem value="skip">{ACTION_LABELS.skip}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {action === 'merge' && <TableHead className="w-20">Primary</TableHead>}
              <TableHead>Customer #</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Invoice Contact</TableHead>
              <TableHead className="hidden md:table-cell">City / State</TableHead>
              <TableHead className="hidden md:table-cell">Terms</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cluster.rows.map((r) => (
              <TableRow key={r.row_index} className={action === 'merge' && r.row_index === primary ? 'bg-emerald-50/60' : ''}
                data-testid={`cluster-${cluster.cluster_id}-row-${r.row_index}`}>
                {action === 'merge' && (
                  <TableCell>
                    <input
                      type="radio"
                      name={`primary-${cluster.cluster_id}`}
                      checked={r.row_index === primary}
                      onChange={() => onDecision({ action, primary_row_index: r.row_index })}
                      className="w-4 h-4 accent-brand-wordmark cursor-pointer"
                      data-testid={`cluster-${cluster.cluster_id}-primary-${r.row_index}`}
                    />
                  </TableCell>
                )}
                <TableCell className="font-mono text-xs">{r.customer_number}</TableCell>
                <TableCell className="text-sm">{r.name}</TableCell>
                <TableCell className="text-sm text-slate-600">{r.invoice_contact || '—'}</TableCell>
                <TableCell className="hidden md:table-cell text-sm text-slate-600">{[r.city, r.state].filter(Boolean).join(', ') || '—'}</TableCell>
                <TableCell className="hidden md:table-cell text-xs text-slate-500">{r.terms || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {action === 'merge' && (
        <p className="text-xs text-slate-500 mt-2">
          The <span className="text-emerald-700 font-medium">primary row</span> supplies address/terms/primary contact. All Customer #s are kept as references; each row's invoice contact becomes an additional contact.
        </p>
      )}
    </div>
  );
}

export default function ClientImportTab() {
  const canImport = useCap('clients', 'add');
  const [file, setFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [committed, setCommitted] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f && f.name.toLowerCase().endsWith('.csv')) {
      setFile(f);
      setPreview(null);
      setCommitted(null);
    } else {
      toast.error('Please upload a .csv export');
    }
  };

  const runPreview = async () => {
    if (!file) return;
    setPreviewLoading(true);
    setPreview(null);
    setCommitted(null);
    setErrorMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await clientsAPI.importPreview(fd);
      setPreview(data);
      const init = {};
      [...data.will_merge, ...data.needs_review].forEach(c => {
        init[c.cluster_id] = { action: c.suggested_action, primary_row_index: c.suggested_primary_index };
      });
      setDecisions(init);
      toast.success(`Preview ready: ${data.totals.will_create} creates, ${data.totals.will_merge} merge clusters, ${data.totals.needs_review} need review`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.userMessage || 'Preview failed';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const runConfirm = async () => {
    if (!preview?.preview_id) return;
    const mergeCount = Object.values(decisions).filter(d => d.action === 'merge').length;
    const skipCount = Object.values(decisions).filter(d => d.action === 'skip').length;
    if (!window.confirm(
      `Apply this import?\n\n` +
      `• ${preview.totals.will_create} new client(s) will be created\n` +
      `• ${mergeCount} duplicate cluster(s) will be merged into single clients\n` +
      `• ${skipCount} cluster(s) will be skipped\n\nContinue?`
    )) return;
    setConfirmLoading(true);
    try {
      const payload = {
        preview_id: preview.preview_id,
        decisions: Object.entries(decisions).map(([cluster_id, d]) => ({ cluster_id, ...d })),
      };
      const { data } = await clientsAPI.importConfirm(payload);
      setCommitted(data);
      setPreview(null);
      setFile(null);
      toast.success(`Import complete: ${data.created_count} clients created (${data.merged_clusters} merged clusters)`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.userMessage || 'Import failed';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setConfirmLoading(false);
    }
  };

  if (!canImport) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 p-6 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/60" data-testid="client-import-denied">
            <Lock className="w-6 h-6 text-slate-400" />
            <div>
              <p className="font-medium text-slate-700">Import not available for your role</p>
              <p className="text-sm text-slate-500">Client import requires the <code className="px-1 py-0.5 bg-slate-200 rounded text-xs">clients:add</code> capability.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-import-tab">
      <Card>
        <CardHeader>
          <CardTitle>WinTeam AR Customer Master Import</CardTitle>
          <CardDescription>
            Upload the AR Customer Master (.csv). Only rows where <code className="bg-slate-100 rounded px-1">Active = 1</code> are imported.
            WinTeam duplicates a customer row per invoice contact — duplicate clusters are collapsed into one client (all Customer #s kept
            as references, each contact preserved as an invoice recipient). Nothing is written until you confirm.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <label className="flex-1 cursor-pointer" data-testid="client-file-label">
              <div className={`flex items-center justify-center gap-3 p-6 border-2 border-dashed rounded-xl transition-colors ${file ? 'border-brand-wordmark bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <Upload className="w-6 h-6 text-slate-400" />
                <div className="text-center">
                  {file ? (
                    <p className="font-medium text-brand-wordmark">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>
                  ) : (
                    <>
                      <p className="font-medium text-slate-700">Click to select .csv export</p>
                      <p className="text-sm text-slate-500">AR Report Customer Master.csv or similar</p>
                    </>
                  )}
                </div>
              </div>
              <input type="file" accept=".csv" onChange={handleFileChange} className="hidden" data-testid="client-file-input" />
            </label>
            <Button
              onClick={runPreview}
              disabled={!file || previewLoading}
              className="btn-navy h-12 px-6"
              data-testid="client-preview-btn"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
              {previewLoading ? 'Parsing...' : 'Run Dry-Run Preview'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {errorMsg && (
        <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="client-import-error">
          <span>{errorMsg}</span>
        </div>
      )}

      {preview && (
        <div className="space-y-4" data-testid="client-import-preview">
          <Card>
            <CardHeader>
              <CardTitle>Dry-Run Preview</CardTitle>
              <CardDescription>Review each bucket below. Nothing has been written yet.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{preview.totals.parsed_active}</p>
                  <p className="text-sm text-slate-500">Active Rows</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-600">{preview.totals.will_create}</p>
                  <p className="text-sm text-slate-500">Will Create</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{preview.totals.will_merge}</p>
                  <p className="text-sm text-slate-500">Will Merge</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{preview.totals.needs_review}</p>
                  <p className="text-sm text-slate-500">Needs Review</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-500">{preview.totals.skipped}</p>
                  <p className="text-sm text-slate-500">Skipped</p>
                </div>
              </div>
              <Button
                onClick={runConfirm}
                disabled={confirmLoading || (preview.totals.will_create + preview.totals.will_merge + preview.totals.needs_review) === 0}
                className="btn-navy w-full h-12"
                data-testid="client-confirm-btn"
              >
                {confirmLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {confirmLoading ? 'Writing...' : 'Confirm & Import'}
              </Button>
            </CardContent>
          </Card>

          {preview.will_merge.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <GitMerge className="w-5 h-5" /> Will Merge ({preview.will_merge.length} clusters)
                </CardTitle>
                <CardDescription>
                  WinTeam duplicated these rows to attach multiple invoice contacts. Each cluster collapses into ONE client.
                  Pick the primary row (source of address/terms) — every contact is preserved.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {preview.will_merge.map(c => (
                  <ClusterCard
                    key={c.cluster_id}
                    cluster={c}
                    decision={decisions[c.cluster_id]}
                    onDecision={(d) => setDecisions(prev => ({ ...prev, [c.cluster_id]: d }))}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {preview.needs_review.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" /> Needs Review ({preview.needs_review.length} clusters)
                </CardTitle>
                <CardDescription>
                  Ambiguous matches — identical names with differing Customer #s, or near-identical names that may be distinct
                  entities. Decide for each: merge into one client, import separately, or skip.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {preview.needs_review.map(c => (
                  <ClusterCard
                    key={c.cluster_id}
                    cluster={c}
                    decision={decisions[c.cluster_id]}
                    onDecision={(d) => setDecisions(prev => ({ ...prev, [c.cluster_id]: d }))}
                    reviewMode
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {preview.will_create.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700">
                  <PlusCircle className="w-5 h-5" /> Will Create ({preview.will_create.length})
                </CardTitle>
                <CardDescription>New single clients with no duplicates detected.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer #</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="hidden md:table-cell">City / State</TableHead>
                      <TableHead>Tax</TableHead>
                      <TableHead className="hidden md:table-cell">Terms</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.will_create.slice(0, 100).map((r, i) => (
                      <TableRow key={r.row_index} data-testid={`client-create-row-${i}`}>
                        <TableCell className="font-mono text-xs">{r.customer_number}</TableCell>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-slate-600">{[r.city, r.state].filter(Boolean).join(', ') || '—'}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{r.tax_status}</Badge></TableCell>
                        <TableCell className="hidden md:table-cell text-xs text-slate-500">{r.terms || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {preview.will_create.length > 100 && (
                  <p className="text-xs text-slate-500 mt-2">…and {preview.will_create.length - 100} more</p>
                )}
              </CardContent>
            </Card>
          )}

          {preview.skipped.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-700 text-base">
                  <FileWarning className="w-5 h-5" /> Skipped ({preview.skipped.length})
                </CardTitle>
                <CardDescription>Already exist in the Clients module (matched by Customer # or name).</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm space-y-1">
                  {preview.skipped.slice(0, 50).map((s, i) => (
                    <li key={i} className="text-slate-600">
                      <span className="font-medium">{s.name}</span>
                      <span className="text-xs text-slate-400"> — {s.reason} ({(s.customer_numbers || []).join(', ')})</span>
                    </li>
                  ))}
                </ul>
                {preview.skipped.length > 50 && <p className="text-xs text-slate-500 mt-2">…and {preview.skipped.length - 50} more</p>}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {committed && (
        <Card className="border-emerald-200" data-testid="client-import-result">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" /> Import Complete
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-emerald-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-emerald-600">{committed.created_count}</p>
                <p className="text-sm text-slate-500">Clients Created</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-blue-600">{committed.merged_clusters}</p>
                <p className="text-sm text-slate-500">Clusters Merged</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-slate-500">{committed.skipped_clusters}</p>
                <p className="text-sm text-slate-500">Clusters Skipped</p>
              </div>
            </div>
            {(committed.conflicts || []).length > 0 && (
              <p className="text-xs text-amber-700 mt-3">
                ⚠️ {committed.conflicts.length} row(s) skipped due to Customer # conflicts: {committed.conflicts.slice(0, 5).join(', ')}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
